/**
 * analytics — the ONE instrumentation seam (measurement plan §0b).
 *
 * The P4 command modules are the only callers: each emits exactly one
 * object-action event on success, with properties = its typed input/output.
 * There are NO UI-level `track()` calls — features instrument through commands.
 * See `TRACKING-PLAN.md` for the event catalogue.
 *
 * There is no Segment *web* write key in this repo today (the backend has a
 * server-side one; the browser has none — flagged as an @radicaled ask in
 * TRACKING-PLAN.md). So this seam does NOT bundle analytics.js or invent a key.
 * It routes every event to a swappable {@link AnalyticsSink}:
 *   • default — buffers in-memory + `console.debug` in dev (nothing leaves the
 *     browser), so events are inspectable and the headless proof can assert them;
 *   • when `BUN_PUBLIC_SEGMENT_WRITE_KEY` is provisioned, wire an analytics.js
 *     sink here (single change, one file) — the call sites never change.
 */

export interface AnalyticsEvent {
  /** Object-action name, e.g. "Candidates Generated". */
  event: string;
  properties: Record<string, unknown>;
  /** Epoch ms. */
  ts: number;
}

export type AnalyticsSink = (event: AnalyticsEvent) => void;

const buffer: AnalyticsEvent[] = [];

const defaultSink: AnalyticsSink = (e) => {
  buffer.push(e);
  if (buffer.length > 500) buffer.shift();
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug(`[analytics] ${e.event}`, e.properties);
  }
};

let sink: AnalyticsSink = defaultSink;

/** Swap the sink (headless proof / a future analytics.js wiring). */
export function setAnalyticsSink(next: AnalyticsSink): void {
  sink = next;
}

/** Restore the default in-memory + console sink. */
export function resetAnalyticsSink(): void {
  sink = defaultSink;
}

/** Events buffered by the default sink (inspection / proof). */
export function recordedEvents(): readonly AnalyticsEvent[] {
  return buffer;
}

/**
 * Emit ONE analytics event. Object-action name; properties should mirror a
 * command's typed schema. Never throws — instrumentation must not break a flow.
 */
export function track(event: string, properties: Record<string, unknown> = {}): void {
  try {
    sink({ event, properties, ts: Date.now() });
  } catch {
    /* analytics must never break the caller */
  }
}
